import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import { MdAdd, MdRefresh } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';

import { Action } from 'helpers';
import { useStyles } from './styles';
import { loadZone } from 'shared/loadZoneSlice';
import { loadItem } from 'features/common/editItemSlice';
import SearchWithFilters from 'components/SearchWithFilters';
import { getAsset } from 'features/assets/assetManagerSlice';
import { tableStyles } from 'components/Tables/TableCells/styles';
import { debounce } from 'GeminiViewerComponent/helpers/lodashUtils';
import { deleteKey } from 'GeminiViewerComponent/helpers/cacheStore';
import { fetchTags } from '../../../../../src/features/tags/tagsSlice';
import { LoadingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';
import { fetchAllHotspotIcons, getHotspotStatus } from 'shared/hotspotsSlice';
import PopupAction from 'components/Misc/PopupActions/components/PopupAction';
import { makeButtonStyles } from '../../../../GeminiViewerComponent/components/styles';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { ResizableTable } from 'GeminiViewerComponent/components/Tables/ResizableTable';
import { EnhancedTableToolbar } from 'GeminiViewerComponent/components/EnhancedTableToolbar';
import { displayProcedureContentVersion } from 'GeminiViewerComponent/features/globals/visibilitySlice';
import { selectVisualEditZone, setVisualEditZone } from 'features/common/editZoneSlice';
import { activateLoading, deactivateLoading } from 'GeminiViewerComponent/features/globals/loadingProgressSlice';
import {
    setDefaultItemPanelSection,
    setViewContentPanel,
} from 'GeminiViewerComponent/features/contentPanel/contentPanelItemSlice';
import { closePanel, openPanel, selectFormInfo, setEditObjectId } from 'features/common/formSlice';
import {
    resizableLabelHeader,
    resizableLabelCell,
    resizableActionCell,
    resizableCheckCell,
    resizableCheckHeaderCell,
    resizableUsageDisplayCell,
    resizableChipsCell,
} from 'components';

function ItemTagTable({ tags, pageInfo, itemTagPayload, setItemTagPayload, sortDirection, sortBy, handleSort }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useSelector(selectDefaultTheme);
    const buttonStyles = makeButtonStyles(theme);
    const classes = useStyles();
    const tableClasses = tableStyles();
    const [selected, setSelected] = useState([]);
    const [selectedAll, setSelectedAll] = React.useState(false);

    const loadedRowsMap = useSelector((state) => state.tags.loadedTagsMap);
    const hotspotStatus = useSelector(getHotspotStatus);
    const visualEditZone = useSelector(selectVisualEditZone);
    const isFormOpen = useSelector(selectFormInfo);

    const headerHeight = 40;
    const rowHeight = 60;

    const isSelected = (id) => selected.indexOf(id) !== -1;
    const user = JSON.parse(localStorage.getItem('user'));

    const prevSelectedAll = usePrevious(selectedAll);
    if (prevSelectedAll && selectedAll) {
        if (selected.length !== tags.length) {
            setSelected(tags.map((row) => row.tag_id));
            setSelectedAll(true);
        }
    }

    const onRefreshIconClick = () => {
        deleteKey(`tags_${user.user_id}`);
        dispatch(fetchTags({ ...itemTagPayload }));
    };

    const handleClick = (event, tag_id) => {
        event.stopPropagation();
        const selectedIndex = selected.indexOf(tag_id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, tag_id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }
        setSelected(newSelected);
        setSelectedAll(newSelected.length === tags.length);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelectedIds = tags.map((tag) => tag.tag_id);
            setSelected(newSelectedIds);
            setSelectedAll(true);
            return;
        }
        setSelected([]);
        setSelectedAll(false);
    };

    function usePrevious(value) {
        const ref = useRef();
        useEffect(() => {
            ref.current = value;
        });
        return ref.current;
    }

    const handleRowClick = async ({ rowData }) => {
        await dispatch(closePanel({ formKey: 'itemTagForm' }));
        if (rowData?.tag_id) {
            await dispatch(
                setEditObjectId({
                    formKey: `itemTagForm`,
                    object_id: rowData?.tag_id,
                })
            );
            dispatch(openPanel({ formKey: `itemTagForm`, formAction: 'Edit' }));
        }
    };

    const clearLastViewerProperties = async () => {
        await dispatch(setVisualEditZone(false));
        await dispatch(setViewContentPanel(false));
        await dispatch(setDefaultItemPanelSection(0));
        await dispatch(displayProcedureContentVersion(null));
        await dispatch(
            closePanel({
                formKey: 'zoneForm',
                formAction: 'Edit',
                isContentPanelOpen: false,
            })
        );
        await dispatch(
            closePanel({
                formKey: 'itemForm',
                formAction: 'Edit',
                isContentPanelOpen: false,
            })
        );
        await dispatch(
            closePanel({
                formKey: 'contentForm',
                formAction: 'Edit',
                isContentPanelOpen: false,
            })
        );
        if (window.geminiViewManager) {
            window.geminiViewManager.updateView();
        }
    };

    const handleItemClick = async (event, zone_id, hotspot, link = null) => {
        event.stopPropagation();
        isFormOpen?.zoneForm?.isOpen && dispatch(closePanel({ formKey: 'zoneForm', isContentPanelOpen: true }));
        isFormOpen?.itemForm?.isOpen && dispatch(closePanel({ formKey: 'itemForm', isContentPanelOpen: true }));

        await dispatch(setViewContentPanel(true));
        if (link !== null) {
            await dispatch(setDefaultItemPanelSection(link.link_type_id));
        }
        await dispatch(
            loadItem({
                zoneId: zone_id,
                itemId: hotspot.item_id,
            })
        );
    };

    const handleAssetClick = async (event, asset_id = null) => {
        event.stopPropagation();
        if (asset_id) {
            if (visualEditZone === true) {
                clearLastViewerProperties();
            }
            navigate(`/asset/${asset_id}`);
        }
    };

    const handleZoneClick = async (event, rowData, assetId, item = null, link = null) => {
        event.stopPropagation();
        dispatch(activateLoading());
        if (visualEditZone === true) {
            await clearLastViewerProperties();
        }
        dispatch(
            closePanel({
                formKey: 'contentForm',
            })
        );
        await dispatch(getAsset(assetId));
        if (hotspotStatus === LoadingStatus.Idle) {
            await dispatch(fetchAllHotspotIcons());
        }
        await dispatch(loadZone({ zoneId: rowData?.zone_id, refresh: false }));
        await dispatch(setVisualEditZone(true));
        if (item !== null) {
            handleItemClick(rowData.zone_id, item, link);
        }
        dispatch(deactivateLoading());
    };

    const columns = [
        {
            accessorKey: 'tag_id',
            size: 50,
            minSize: 50,
            enableResizing: false,
            label: '',
            padding: 'checkbox',
            cellLevel: 'tag',
            numSelected: selected.length,
            rowCount: tags.length,
            handleClick: handleClick,
            handleSelectAllClick: handleSelectAllClick,
            isSelected: isSelected,
            header: (info) => resizableCheckHeaderCell(null, info?.header?.column?.columnDef),
            cell: (info) => resizableCheckCell({ rowData: info?.row?.original }, info?.column?.columnDef),
        },
        {
            accessorKey: 'tag_id',
            size: 50,
            minSize: 50,
            enableResizing: false,
            padding: 'none',
            label: '',
            actionCellLevel: 'tag',
            targetPopup: 'tagManagement',
            formKey: 'itemTagForm',
            dispatch: dispatch,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableActionCell({ rowData: info?.row?.original }, info?.column?.columnDef),
        },
        {
            accessorKey: 'name',
            flexGrow: 1,
            size: 250,
            minSize: 250,
            label: 'Name',
            padding: 'normal',
            sortDirection: sortDirection,
            sortBy: sortBy,
            handleSort: handleSort,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableLabelCell(info?.row?.original, info?.column?.columnDef),
        },
        {
            accessorKey: 'assets',
            flexGrow: 1,
            size: 80,
            minSize: 80,
            enableResizing: false,
            label: 'Assets',
            displayDataKey: 'asset_name',
            padding: 'normal',
            onClick: handleAssetClick,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) =>
                resizableUsageDisplayCell({ rowData: info?.row?.original?.tag_usage }, info?.column?.columnDef),
        },
        {
            accessorKey: 'zones',
            flexGrow: 1,
            size: 80,
            minSize: 80,
            enableResizing: false,
            label: 'Zones',
            displayDataKey: 'zone_name',
            padding: 'normal',
            onClick: handleZoneClick,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) =>
                resizableUsageDisplayCell({ rowData: info?.row?.original?.tag_usage }, info?.column?.columnDef),
        },
        {
            accessorKey: 'items',
            flexGrow: 1,
            size: 80,
            minSize: 80,
            enableResizing: false,
            label: 'Items',
            displayDataKey: 'item_name',
            padding: 'normal',
            onClick: handleZoneClick,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) =>
                resizableUsageDisplayCell({ rowData: info?.row?.original?.tag_usage }, info?.column?.columnDef),
        },
        {
            accessorKey: 'content',
            flexGrow: 1,
            size: 80,
            minSize: 80,
            enableResizing: false,
            label: 'Content',
            displayDataKey: 'content_name',
            padding: 'normal',
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) =>
                resizableUsageDisplayCell({ rowData: info?.row?.original?.tag_usage }, info?.column?.columnDef),
        },
        {
            accessorKey: 'groups',
            flexGrow: 1,
            size: 250,
            minSize: 250,
            enableResizing: false,
            label: 'Groups',
            displayDataKey: 'display_name',
            padding: 'normal',
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableChipsCell({ rowData: info?.row?.original }, info?.column?.columnDef),
        },
    ];

    // eslint-disable-next-line no-unused-vars
    function loadMoreRows({ startIndex, stopIndex }) {}

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(
        debounce((searchString) => {
            setItemTagPayload({
                ...itemTagPayload,
                searchString: searchString,
            });
        }, 1000),
        [itemTagPayload]
    );

    const handleCreateNew = () => {
        dispatch(
            openPanel({
                formKey: 'itemTagForm',
                formAction: 'Create',
                clearEditId: true,
            })
        );
    };

    return (
        <div className={classes.root}>
            <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                <Tooltip title="Add New Tag">
                    <IconButton onClick={handleCreateNew} size="large" className={buttonStyles.contentAddButton}>
                        <MdAdd className="react-icon" />
                    </IconButton>
                </Tooltip>
                <div style={{ flexGrow: 1 }}></div>
                <IconButton
                    onClick={onRefreshIconClick}
                    aria-label="close"
                    sx={{
                        color: '#707070',
                    }}
                    size="large"
                >
                    <MdRefresh className="react-icon" style={{ fontSize: '18px', color: 'black' }} />
                </IconButton>
                <SearchWithFilters filter={false} onChange={(evt) => debouncedSearch(evt.target.value)} />
            </div>
            {selected.length > 0 && (
                <EnhancedTableToolbar numSelected={selected.length} toolTipTitle={'Delete'} totalCount={tags?.length}>
                    <PopupAction
                        action={Action.DeleteArray}
                        object={{ tagIds: selected, setTagIds: setSelected }}
                        level={'tag'}
                        showLabel={false}
                    />
                </EnhancedTableToolbar>
            )}
            <div
                style={{
                    height: `calc(100vh - ${selected.length > 0 ? '345px' : '280px'})`,
                    minWidth: '500px',
                }}
            >
                <ResizableTable
                    initialRows={tags}
                    allRows={tags}
                    totalRowCount={pageInfo.TotalCount}
                    loadedRowsMap={loadedRowsMap}
                    loadMoreRows={loadMoreRows}
                    columns={columns}
                    cellClassName={tableClasses.flexContainer}
                    className={tableClasses.table}
                    rowClassName={tableClasses.flexContainer}
                    headerHeight={headerHeight}
                    rowHeight={rowHeight}
                    onRowClick={handleRowClick}
                />
            </div>
        </div>
    );
}

export { ItemTagTable };
